import { ReactNode } from 'react';
import { useAuthCheck } from '@/hooks/queries/useAuthQueries';
import { useAuthStore } from '@/stores/authStore';
import { localStorageUtils } from '@/utils/localStorage';

type ProtectedRouteProps = {
  children: ReactNode;
};

/**
 * ProtectedRoute wrapper
 * Protects routes that require authentication
 * Usage: <Route path="/pools" element={<ProtectedRoute><PoolsPage /></ProtectedRoute>} />
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated } = useAuthStore();
  const { isLoading: authCheckLoading } = useAuthCheck();

  // Derive isLoading: if no session, we're immediately done; if session flag is set, use query loading state
  const hasSession = localStorageUtils.isSessionAuthenticated();
  const isLoading = hasSession && authCheckLoading;

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, show page does not exist (AuthNavigator will handle redirect)
  if (!isAuthenticated) {
    return <PageDoesNotExist />;
  }

  return <>{children}</>;
}

/**
 * Page does not exist component
 */
function PageDoesNotExist() {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">Page does not exist</h1>
        <p className="text-gray-600">This page requires authentication or does not exist.</p>
      </div>
    </div>
  );
}
